import React from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { LinearProgress } from '@mui/material'
import { teacherApiSlice } from '../../../features/teacher/teacherSlice'
import { userApiSlice } from '../../../features/user/userSlice'
import { H3 } from '../../../layouts/TextStyles'
import AddTeacherDialog from './components/AddTeacherDialog'
import TeachersTable from './components/TeachersTable'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'

const TeacherListPage = () => {
    const { formatMessage } = useIntl()
    const [fetchTeachers] = teacherApiSlice.useLazyFetchAllTeachersQuery()
    const [deleteTeacher, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()

    const { rows, isLoading, loadedPages, sentinelRef, reload } =
        usePaginatedList(
            (page, size) =>
                fetchTeachers({
                    query: `page=${page}&size=${size}`,
                    roles: ['TEACHER', 'CURATOR'],
                }).unwrap(),
            [],
        )

    const handleDelete = async (id: number) => {
        try {
            await deleteTeacher(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (error) {}
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            reload()
        } catch (error) {}
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'teachers' })}</H3>
                    </Title>

                    <AddTeacherDialog />
                </Header>

                {(isLoading && loadedPages === 0) || isReseting || isDeleting ? (
                    <LinearProgress />
                ) : (
                    <>
                        <TeachersTable
                            isFetching={isLoading || isReseting}
                            teachersData={rows}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                        />

                        <Sentinel ref={sentinelRef} isLoading={isLoading} />
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default TeacherListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
